import type { ServiceJobDetailRow } from './reports';

export type ReportColumnValue = string | number | boolean | null;
export type ReportColumnLocale = 'th' | 'en';

/** Immutable v1 export order, including queued snapshots without a column selection. */
export const SERVICE_JOB_DETAIL_V1_COLUMN_IDS = [
  'trip_id',
  'trip_ref',
  'trip_status',
  'trip_leg',
  'service_mode',
  'dispatch_mode',
  'created_at',
  'requested_at',
  'service_date',
  'date_basis',
  'scheduled_start',
  'scheduled_end',
  'accepted_at',
  'started_at',
  'picked_up_at',
  'completed_at',
  'passenger_id',
  'passenger_code',
  'passenger_name',
  'passenger_phone',
  'agent_id',
  'agent_code',
  'agent_name',
  'agent_phone',
  'vehicle_id',
  'vehicle_plate',
  'provider_id',
  'provider_name',
  'service_area_ids',
  'service_area_names',
  'service_group_id',
  'service_group_name',
  'origin_label',
  'destination_label',
  'planned_distance_km',
  'planned_duration_min',
  'actual_distance_km',
  'actual_duration_min',
  'actual_distance_source',
  'actual_duration_source',
  'currency',
  'fare_state',
  'quote_provenance',
  'quoted_total',
  'current_total',
  'final_total',
  'fare_adjustment_delta',
  'fare_base',
  'fare_overage_distance',
  'fare_overage_time',
  'fare_surcharge_total',
  'fare_computed_at',
  'billing_distance_source',
  'billing_duration_source',
  'billable_distance_km',
  'billable_duration_min',
  'fare_is_free',
  'base_payment_status',
  'base_amount_due',
  'base_amount_received',
  'base_outstanding',
  'base_due_by',
  'base_verified_at',
  'overage_payment_status',
  'overage_amount_due',
  'overage_amount_received',
  'overage_outstanding',
  'overage_due_by',
  'overage_verified_at',
  'billed_total',
  'received_total',
  'outstanding_total',
  'payment_state',
  'requires_finance_review',
] as const;

/** Current report order. Keep v1 immutable when extending the contract. */
export const SERVICE_JOB_DETAIL_V2_COLUMN_IDS = [
  ...SERVICE_JOB_DETAIL_V1_COLUMN_IDS,
  'has_location_exception',
  'exception_count',
  'latest_exception_at',
  'latest_exception_event',
  'latest_exception_warning',
  'latest_exception_override_reason',
  'latest_exception_confirmation',
  'latest_exception_source',
  'latest_exception_accuracy_m',
  'latest_exception_distance_to_target_m',
  'latest_exception_radius_m',
] as const;

export const SERVICE_JOB_DETAIL_V3_COLUMN_IDS = [
  ...SERVICE_JOB_DETAIL_V2_COLUMN_IDS,
  'waived_outstanding_total',
  'overage_review_status',
] as const;

export const SERVICE_JOB_DETAIL_COLUMN_IDS = [
  ...SERVICE_JOB_DETAIL_V3_COLUMN_IDS,
  'completed_refunds_total',
  'net_retained_total',
  'pending_refunds_total',
  'reconciliation_cash_total',
] as const;

export type ServiceJobDetailColumnId = (typeof SERVICE_JOB_DETAIL_COLUMN_IDS)[number];

export const DEFAULT_SERVICE_JOB_DETAIL_COLUMN_IDS: readonly ServiceJobDetailColumnId[] = [
  'trip_ref',
  'trip_status',
  'service_date',
  'passenger_name',
  'agent_name',
  'provider_name',
  'service_area_names',
  'service_group_name',
  'fare_state',
  'quoted_total',
  'current_total',
  'final_total',
  'billed_total',
  'received_total',
  'outstanding_total',
  'payment_state',
  'requires_finance_review',
];

export function serviceJobDetailColumnLabelKey(id: ServiceJobDetailColumnId) {
  return `dictionaryFieldLabel_${id}` as const;
}

function bangkokTimestamp(value: string | null): string | null {
  if (value === null) return null;
  return new Date(new Date(value).getTime() + 7 * 60 * 60 * 1000).toISOString().replace('Z', '+07:00');
}

const COLUMN_ACCESSORS = {
  has_location_exception: (row) => row.hasLocationException,
  exception_count: (row) => row.exceptionCount,
  latest_exception_at: (row) => bangkokTimestamp(row.latestExceptionAt),
  latest_exception_event: (row) => row.latestExceptionEvent ?? null,
  latest_exception_warning: (row) => row.latestExceptionWarning ?? null,
  latest_exception_override_reason: (row) => row.latestExceptionOverrideReason ?? null,
  latest_exception_confirmation: (row) => row.latestExceptionConfirmation ?? null,
  latest_exception_source: (row) => row.latestExceptionSource ?? null,
  latest_exception_accuracy_m: (row) => row.latestExceptionAccuracyM ?? null,
  latest_exception_distance_to_target_m: (row) => row.latestExceptionDistanceToTargetM ?? null,
  latest_exception_radius_m: (row) => row.latestExceptionRadiusM ?? null,

  trip_id: (row) => row.tripId,
  trip_ref: (row) => row.tripRef,
  trip_status: (row) => row.tripStatus,
  trip_leg: (row) => row.tripLeg,
  service_mode: (row) => row.serviceMode,
  dispatch_mode: (row) => row.dispatchMode,
  created_at: (row) => bangkokTimestamp(row.createdAt),
  requested_at: (row) => bangkokTimestamp(row.requestedAt),
  service_date: (row) => bangkokTimestamp(row.serviceDate),
  date_basis: (row) => row.serviceDateBasis,
  scheduled_start: (row) => bangkokTimestamp(row.scheduledStart),
  scheduled_end: (row) => bangkokTimestamp(row.scheduledEnd),
  accepted_at: (row) => bangkokTimestamp(row.acceptedAt),
  started_at: (row) => bangkokTimestamp(row.startedAt),
  picked_up_at: (row) => bangkokTimestamp(row.pickedUpAt),
  completed_at: (row) => bangkokTimestamp(row.completedAt),
  passenger_id: (row) => row.passenger.id,
  passenger_code: (row) => row.passenger.code,
  passenger_name: (row) => row.passenger.name,
  passenger_phone: (row) => row.passenger.phone,
  agent_id: (row) => row.agent?.id ?? null,
  agent_code: (row) => row.agent?.code ?? null,
  agent_name: (row) => row.agent?.name ?? null,
  agent_phone: (row) => row.agent?.phone ?? null,
  vehicle_id: (row) => row.vehicle?.id ?? null,
  vehicle_plate: (row) => row.vehicle?.plate ?? null,
  provider_id: (row) => row.provider.id,
  provider_name: (row) => row.provider.name,
  service_area_ids: (row) => row.serviceAreas.map((area) => area.id).join('|'),
  service_area_names: (row) => row.serviceAreas.map((area) => area.name).join('|'),
  service_group_id: (row) => row.serviceGroup.id,
  service_group_name: (row) => row.serviceGroup.name,
  origin_label: (row) => row.originLabel,
  destination_label: (row) => row.destinationLabel,
  planned_distance_km: (row) => row.plannedDistanceKm,
  planned_duration_min: (row) => row.plannedDurationMin,
  actual_distance_km: (row) => row.actualDistanceKm,
  actual_duration_min: (row) => row.actualDurationMin,
  actual_distance_source: (row) => row.actualDistanceSource,
  actual_duration_source: (row) => row.actualDurationSource,
  currency: (row) => row.finance.currency,
  fare_state: (row) => row.finance.fareState,
  quote_provenance: (row) => row.finance.quoteProvenance,
  quoted_total: (row) => row.finance.quotedTotal,
  current_total: (row) => row.finance.currentTotal,
  final_total: (row) => row.finance.finalTotal,
  fare_adjustment_delta: (row) => row.finance.fareAdjustmentDelta,
  fare_base: (row) => row.finance.fareBase,
  fare_overage_distance: (row) => row.finance.fareOverageDistance,
  fare_overage_time: (row) => row.finance.fareOverageTime,
  fare_surcharge_total: (row) => row.finance.fareSurchargeTotal,
  fare_computed_at: (row) => bangkokTimestamp(row.finance.fareComputedAt),
  billing_distance_source: (row) => row.finance.billingDistanceSource,
  billing_duration_source: (row) => row.finance.billingDurationSource,
  billable_distance_km: (row) => row.finance.billableDistanceKm,
  billable_duration_min: (row) => row.finance.billableDurationMin,
  fare_is_free: (row) => row.finance.fareIsFree,
  base_payment_status: (row) => row.finance.basePayment?.status ?? null,
  base_amount_due: (row) => row.finance.basePayment?.amountDue ?? null,
  base_amount_received: (row) => row.finance.basePayment?.amountReceived ?? null,
  base_outstanding: (row) => row.finance.basePayment?.outstanding ?? null,
  base_due_by: (row) => bangkokTimestamp(row.finance.basePayment?.dueBy ?? null),
  base_verified_at: (row) => bangkokTimestamp(row.finance.basePayment?.verifiedAt ?? null),
  overage_payment_status: (row) => row.finance.overagePayment?.status ?? null,
  overage_amount_due: (row) => row.finance.overagePayment?.amountDue ?? null,
  overage_amount_received: (row) => row.finance.overagePayment?.amountReceived ?? null,
  overage_outstanding: (row) => row.finance.overagePayment?.outstanding ?? null,
  overage_due_by: (row) => bangkokTimestamp(row.finance.overagePayment?.dueBy ?? null),
  overage_verified_at: (row) => bangkokTimestamp(row.finance.overagePayment?.verifiedAt ?? null),
  billed_total: (row) => row.finance.billedTotal,
  waived_outstanding_total: (row) => row.finance.waivedOutstandingTotal ?? null,
  overage_review_status: (row) => row.finance.overageReviewStatus ?? null,
  received_total: (row) => row.finance.receivedTotal,
  completed_refunds_total: (row) => row.finance.completedRefundsTotal,
  net_retained_total: (row) => row.finance.netRetainedTotal,
  pending_refunds_total: (row) => row.finance.pendingRefundsTotal,
  reconciliation_cash_total: (row) => row.finance.reconciliationCashTotal,
  outstanding_total: (row) => row.finance.outstandingTotal,
  payment_state: (row) => row.finance.paymentState,
  requires_finance_review: (row) => row.finance.requiresFinanceReview,
} satisfies Record<ServiceJobDetailColumnId, (row: ServiceJobDetailRow) => ReportColumnValue>;

export function serviceJobDetailColumnValue(
  row: ServiceJobDetailRow,
  id: ServiceJobDetailColumnId,
): ReportColumnValue {
  return COLUMN_ACCESSORS[id](row);
}
